import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import {
  UPLOADER_STATUS_WAITING,
  type UploaderState,
} from '@iconik-io/uploader/src/state';

import { agentApiClient } from '../../agent_api_client';
import { isContextMenuEmpty } from '../../components/context_menu/utils';
import MainFilesDropzone from '../../components/main_file_drop_zone/main_file_drop_zone';
import SelectableItems from '../../components/selectable_items/selectable_items';
import { getPlayOriginalDuringTranscoding } from '../../components/unified_player/selectors';
import UploaderDestination from '../../components/uploader_page_destination/uploader_page_destination';
import UploaderReflectDirectoriesCheckbox from '../../components/uploader_reflect_directories_checkbox/uploader_reflect_directories_checkbox';
import { EXTRASMALL_SCREEN } from '../../constants/breakpoints';
import { UPLOADS_PANEL } from '../../constants/data';
import {
  UPLOADER_CLEAR_WARNING,
  UPLOADER_FILES_METADATA_MODAL,
  UPLOADER_STORAGE_CHANGE,
} from '../../constants/modals';
import {
  SHARE_ASSET_PAGE_ROUTE,
  SHARE_COLLECTION_PAGE_ROUTE,
  UPLOAD_PAGE_ROUTE,
} from '../../constants/routes';
import { segmentTrackEvent } from '../../ducks/analytics_tracking/thunks';
import { historyGoBack, historyPush } from '../../ducks/history/actions';
import { openModal } from '../../ducks/modals_manager/slice';
import { getSearchItems } from '../../ducks/pages/search/thunks';
import { handleFilesToUpload } from '../../ducks/pages/uploader/thunks';
import { searchFilterActions } from '../../ducks/panels/search_filters/actions';
import { closePanel } from '../../ducks/panels/thunks';
import { clearFinishedUploads } from '../../ducks/uploader/engine/thunks';
import {
  getIsCollectionsCreation,
  selectUploads,
} from '../../ducks/uploader/selectors';
import {
  uploaderSetExpanded,
  uploaderSetReflectDirectories,
  uploaderSetStorage,
} from '../../ducks/uploader/slice';
import { setInitialCollectionsPath } from '../../ducks/uploader/thunks';
import {
  getUploadAssetId,
  isStagedUpload,
  isUploadedFile,
} from '../../ducks/uploader/upload_state';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import FileButton from '../../ui/forms/fields/file_button/file_button';
import { PageHeaderLabel } from '../../ui/page_header/page_header_label';
import TableView from '../../ui/table/table';
import { createPickedFile } from '../../utils/create_picked_file';
import filesHaveDirectories from '../../utils/files_have_directories';
import { selectUserRoles } from '../../utils/get_user_roles';
import AgentPrompt from './agent_prompt';
import columns from './columns';
import columnsCompact from './columns_compact';
import ContextMenu from './context_menu_items';
import { getDefinition } from './context_menu_panel';
import UploadsHeader from './header';
import { getEntity } from './helpers';
import {
  selectCanUploadByPermissions,
  selectDefaultStorage,
  selectEntityType,
  selectIsUploaderExpanded,
  selectIsUploaderFullScreen,
  selectStorageOptionDisabled,
  selectUploadStorage,
} from './selectors';
import UploaderEmptyState from './uploader_empty_state';

import styles from './uploads_panel_widget.module.css';

export type UploadsWidgetPanelProps = {
  isCompact?: boolean;
  className?: string;
};

export function UploadsWidgetPanel({
  isCompact,
  className,
}: UploadsWidgetPanelProps) {
  const dispatch = useAppDispatch();
  const files = useAppSelector(selectUploads);
  const collections = useAppSelector((state) => state.entities.collections);
  const uploaderCollections = useAppSelector(
    (state) => state.uploaderCollections,
  );
  const roles = useAppSelector((state) => selectUserRoles(state));
  // Route and params come from the same match: see PanelsContainer.
  const { route, params } = useLeafRouteParams();
  const entityId = params.id;
  const versionId = params.versionId;
  const entityType = useAppSelector((state) =>
    selectEntityType(state, entityId, versionId, route),
  );
  const entity = useAppSelector((state) => getEntity(state, entityId));
  const storage = useAppSelector((state) =>
    selectUploadStorage(state, entityId, versionId, route),
  );
  const defaultStorage = useAppSelector((state) =>
    selectDefaultStorage(state, entityId, versionId, route),
  );
  const optionsDisabled = useAppSelector((state) =>
    selectStorageOptionDisabled(state, entityId),
  );
  const reflectDirectories = useAppSelector(
    (state) => state.uploaderReducer.reflectDirectories,
  );
  const isSmallScreen = useAppSelector(
    (state) => state.displayReducer.breakpoint <= EXTRASMALL_SCREEN,
  );
  const fullscreen = useAppSelector((state) =>
    selectIsUploaderFullScreen(state, entityId, versionId, route),
  );
  const expanded = useAppSelector((state) => selectIsUploaderExpanded(state));
  const canUpload = useAppSelector((state) =>
    selectCanUploadByPermissions(state, entityId),
  );
  const isShare = [
    SHARE_COLLECTION_PAGE_ROUTE,
    SHARE_ASSET_PAGE_ROUTE,
  ].includes(route);
  const contextMenuEmpty = useAppSelector((state) =>
    isContextMenuEmpty(getDefinition, state),
  );
  const isCollectionsCreationInProgress = useAppSelector((state) =>
    getIsCollectionsCreation(state),
  );
  const hasDirectories = useAppSelector((state) =>
    filesHaveDirectories(Object.values(state.uploaderItems)),
  );
  useEffect(() => {
    if (!roles.canCreateCollections && reflectDirectories) {
      uploaderSetReflectDirectories(false);
    }
  });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [seeAll, setSeeAll] = useState<boolean>(isShare);
  const [closeAgentPrompt, setCloseAgentPrompt] = useState<boolean>(
    localStorage.getItem('closeAgentPrompt') === 'true',
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isLoadingEntity = Boolean(!entity?.id && entityType);
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (!canUpload || isLoadingEntity) {
        return;
      }

      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }

      const fileItems = Array.from(items)
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);

      if (fileItems.length > 0 && fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        for (const file of fileItems) {
          dataTransfer.items.add(file);
        }

        fileInputRef.current.files = dataTransfer.files;
        fileInputRef.current.dispatchEvent(
          new Event('change', { bubbles: true }),
        );
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [canUpload, isLoadingEntity]);

  const shouldReadFileMetadata = useAppSelector(
    getPlayOriginalDuringTranscoding,
  );
  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.from(event.target.files ?? []).map((file: File) =>
        createPickedFile(file, shouldReadFileMetadata),
      );
      dispatch(handleFilesToUpload(newFiles, entityType, entity));
    },
    [entityType, entity, shouldReadFileMetadata],
  );

  const handleChangeDestination = (
    storageId: string,
    uploads: UploaderState[],
  ) => {
    dispatch(uploaderSetStorage(storageId));
    dispatch(
      segmentTrackEvent({
        eventName: 'Update Upload Storage',
      }),
    );
    const filesToUpdate = uploads.filter((upload) => {
      if (
        !isStagedUpload(upload) &&
        upload.status !== UPLOADER_STATUS_WAITING
      ) {
        return false;
      }
      const collectionId = upload.options.baseCollectionId;
      if (
        // for mapped collections the storage should stick to the collection storage
        collectionId &&
        collections[collectionId].storage_id
      ) {
        return false;
      }
      return true;
    });
    if (filesToUpdate.length) {
      dispatch(
        openModal({
          id: UPLOADER_STORAGE_CHANGE,
          contextItems: filesToUpdate,
          options: { storageId },
        }),
      );
    }
  };

  const handleSeeNew = (uploads: UploaderState[], isSeeAll: boolean) => {
    dispatch(closePanel(UPLOADS_PANEL));
    dispatch(searchFilterActions.resetFiltersData());
    dispatch(searchFilterActions.setQuery(''));
    const ids = uploads
      .filter((upload) => getUploadAssetId(upload) && isUploadedFile(upload))
      .flatMap((upload) => {
        if (entityType === OBJECT_TYPE_COLLECTIONS) {
          // when uploading directories to a collections
          // we have to show the direct children of the current collection
          const fileParentCollection = uploaderCollections[upload.id]?.[0].id;
          if (fileParentCollection && fileParentCollection !== entity?.id) {
            return [fileParentCollection];
          }
        }
        const assetId = getUploadAssetId(upload);
        return assetId ? [assetId] : [];
      });
    dispatch(
      searchFilterActions.setFilterValues({
        id: ids,
      }),
    );
    dispatch(searchFilterActions.setFacetValues({}));
    dispatch(searchFilterActions.setQuery(''));
    dispatch(getSearchItems());
    if (entityType !== OBJECT_TYPE_COLLECTIONS || isSeeAll) {
      dispatch(historyPush('/search'));
    }
  };

  const handleAgentPromptClose = () => {
    setCloseAgentPrompt(true);
    localStorage.setItem('closeAgentPrompt', 'true');
  };

  const fullscreenTitle = (
    <>
      Upload
      <PageHeaderLabel value={files.length} />
    </>
  );
  const showEntityTitle = entity && !seeAll;
  const widgetTitle = `Upload ${showEntityTitle ? 'to:' : ''} ${
    showEntityTitle ? entity?.title : ''
  }`;

  const filesList = isLoadingEntity && !seeAll ? [] : files;
  const entityFiles = entity
    ? files.filter(
        (upload) =>
          getUploadAssetId(upload) === entity.id ||
          upload.options.baseCollectionId === entity.id,
      )
    : filesList;

  const currentFiles = seeAll ? files : entityFiles;
  const otherFilesAmount = isLoadingEntity
    ? entityFiles.length
    : files.length - entityFiles.length;
  const Root = fullscreen ? 'div' : MainFilesDropzone;

  const hasWaitingFile = currentFiles.some(isStagedUpload);

  return (
    <Root
      className={classNames(
        {
          [styles.fullscreen]: fullscreen,
          [styles.compact]: isCompact,
        },
        styles.root,
        className,
      )}
    >
      {!isCompact ? (
        <UploadsHeader
          isContextMenuEmpty={contextMenuEmpty}
          isSmallScreen={isSmallScreen}
          canSearch={roles.canSearch}
          fullscreen={fullscreen}
          expanded={expanded}
          title={fullscreen ? fullscreenTitle : widgetTitle}
          onMinimize={() => {
            dispatch(historyGoBack());
          }}
          onClose={() => {
            dispatch(closePanel(UPLOADS_PANEL));
            if (fullscreen) {
              dispatch(historyGoBack());
            }
          }}
          onExpand={
            roles.webCanTopLevelUpload
              ? () => {
                  dispatch(uploaderSetExpanded(true));
                  dispatch(historyPush(UPLOAD_PAGE_ROUTE));
                }
              : undefined
          }
          onClear={() => {
            if (files.some(isStagedUpload)) {
              dispatch(
                openModal({
                  id: UPLOADER_CLEAR_WARNING,
                }),
              );
            } else {
              dispatch(clearFinishedUploads());
            }
          }}
          items={currentFiles}
          onSeeNew={() => {
            handleSeeNew(currentFiles, seeAll);
          }}
        />
      ) : undefined}
      <div className={styles.content}>
        {otherFilesAmount && !isShare ? (
          <button
            type="button"
            className={classNames({
              [styles.collapsible_button]: true,
              [styles.collapsible_button__see_more]: !seeAll,
              [styles.collapsible_button___see_less]: seeAll,
            })}
            onClick={() => setSeeAll(!seeAll)}
          >
            {seeAll ? 'Show less' : `Show all (+${otherFilesAmount})`}
          </button>
        ) : undefined}
        {currentFiles.length === 0 ? (
          <UploaderEmptyState
            isLoadingEntity={isLoadingEntity}
            onChange={handleFileChange}
            canUpload={canUpload}
          />
        ) : null}
        {currentFiles.length > 0 ? (
          <SelectableItems
            onSelectIds={setSelectedIds}
            items={currentFiles}
            contextMenu={!isCompact ? ContextMenu : undefined}
            className={styles.table_wrapper}
            isSelectAllAllowed={fullscreen}
          >
            <TableView
              className={classNames(styles.table, {
                [styles.two_columns]: isCompact,
              })}
              items={currentFiles}
              selectedIds={selectedIds}
              renderColumns={isCompact ? columnsCompact : columns}
              showHeader={false}
            />
          </SelectableItems>
        ) : null}
      </div>
      <div
        className={classNames(styles.footer, {
          [styles.footer_with_background]: isShare,
        })}
      >
        {!isCompact ? (
          <>
            <Button
              disabled={!hasWaitingFile || isCollectionsCreationInProgress}
              iconName="load_up"
              className={styles.footer_button}
              onClick={() => {
                dispatch(
                  openModal({
                    id: UPLOADER_FILES_METADATA_MODAL,
                    contextItems: currentFiles,
                    options: { onlyRequiredMetadata: true, reflectDirectories },
                  }),
                );
              }}
            >
              Upload
            </Button>
            <FileButton
              ref={fileInputRef}
              name="files"
              onChange={handleFileChange}
              buttonProps={{
                wideBasis: false,
                iconName: 'plus',
                className: styles.footer_button,
                disabled: isLoadingEntity || !canUpload,
              }}
              multiple
            />
            <div className={styles.destination}>
              {!isShare ? (
                <UploaderDestination
                  value={storage}
                  disabled={optionsDisabled || isLoadingEntity}
                  canClear={defaultStorage !== storage}
                  onChange={(storageId) =>
                    handleChangeDestination(storageId, currentFiles)
                  }
                />
              ) : null}
              {roles.canCreateCollections && hasDirectories ? (
                <UploaderReflectDirectoriesCheckbox
                  reflectDirectories={reflectDirectories}
                  onReflectDirectoriesChange={(value) => {
                    dispatch(uploaderSetReflectDirectories(Boolean(value)));
                    dispatch(setInitialCollectionsPath());
                  }}
                />
              ) : undefined}
            </div>
          </>
        ) : undefined}
      </div>
      {!isCompact &&
      !agentApiClient.hasFeature('UPLOAD_BY_POST_REQUEST') &&
      !closeAgentPrompt ? (
        <AgentPrompt
          fullscreen={fullscreen}
          promptText={
            agentApiClient.isReachable
              ? 'Update the iconik Agent for uninterrupted uploads and downloads and more robust organization abilities.'
              : 'Download the iconik Agent for uninterrupted uploads and downloads and more robust organization abilities.'
          }
          closePrompt={handleAgentPromptClose}
        />
      ) : null}
    </Root>
  );
}
